import { motion } from "framer-motion";
import { Link } from "react-router-dom";
// Styles
import "./../../styles/pages/Home.css";

const featuredProjects = [
  {
    number: "01",
    title: "iEar",
    category: "AI • EMBEDDED SYSTEMS",
    description:
      "An assistive sound recognition system that detects important environmental sounds and delivers real-time alerts.",
    tech: ["AI", "Raspberry Pi", "Firebase"],
    visual: "sound / awareness",
  },
  {
    number: "02",
    title: "Visitor Management",
    category: "WEB • MOBILE",
    description:
      "A digital visitor management platform with QR access, role-based controls and administrative tools.",
    tech: ["Next.js", "Flutter", "Supabase"],
    visual: "access / management",
  },
  {
    number: "03",
    title: "Data Automation",
    category: "DATA • AUTOMATION",
    description:
      "Tools that transform operational data into structured, validated and usable business information.",
    tech: ["Python", "Pandas", "SQL"],
    visual: "automate / transform",
  },
];

const services = [
  {
    number: "01",
    title: "Web Applications",
    description:
      "Modern, responsive websites and web platforms built around real business requirements.",
  },
  {
    number: "02",
    title: "Mobile Apps",
    description:
      "Cross-platform mobile experiences designed for usability, performance and growth.",
  },
  {
    number: "03",
    title: "Automation",
    description: "We automate repetitive workflows and connect systems so teams can work smarter.",
  },
  {
    number: "04",
    title: "Data & AI",
    description:
      "Data processing, intelligent tools and machine learning solutions built around practical problems.",
  },
];

const capabilities = [
  "React",
  "Next.js",
  "Flutter",
  "Python",
  "Supabase",
  "Firebase",
  "AI / ML",
  "Automation",
  "IoT",
];

function Home() {
  return (
    <main className="benoma-home bg-dark text-light overflow-hidden">
      {/* HERO */}
      <section className="home-hero position-relative min-vh-100 d-flex align-items-center">
        <div className="home-grid position-absolute top-0 start-0 w-100 h-100" />
        <div className="home-background-word">BENOMA</div>

        <div className="container position-relative py-5">
          <div className="row align-items-center g-5 py-lg-5">
            {/* HERO CONTENT */}
            <div className="col-lg-7">
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="benoma-eyebrow d-inline-block rounded-pill px-3 py-2 mb-4">
                  SOFTWARE • DATA • ENGINEERING
                </span>
              </motion.div>
              <motion.h1
                className="home-title fw-bold mb-4"
                initial={{ opacity: 0, y: 35 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: 0.1 }}
              >
                We turn ideas into
                <span className="d-block text-danger">digital experiences.</span>
              </motion.h1>
              <motion.p
                className="home-description mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.25 }}
              >
                Benoma is a collective of developers, engineers and problem solvers building web
                apps, mobile products, intelligent systems and automation tools.
              </motion.p>
              <motion.div
                className="d-flex flex-column flex-sm-row gap-3 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
              >
                <Link
                  to="/projects"
                  className="btn btn-danger btn-lg d-inline-flex align-items-center justify-content-center gap-2"
                >
                  Explore Our Work
                  <span>→</span>
                </Link>
                <Link
                  to="/contact"
                  className="btn btn-outline-light btn-lg d-inline-flex align-items-center justify-content-center"
                >
                  Start a Project
                </Link>
              </motion.div>
              <motion.div
                className="d-flex flex-wrap gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.65 }}
              >
                {capabilities.map((capability) => (
                  <span key={capability} className="capability-badge rounded-pill px-3 py-2">
                    {capability}
                  </span>
                ))}
              </motion.div>
            </div>
            {/* TERMINAL */}
            <div className="col-lg-5">
              <motion.div
                className="terminal-card position-relative"
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.25 }}
              >
                <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom border-secondary-subtle">
                  <div className="d-flex gap-2">
                    <span className="terminal-dot bg-danger" />
                    <span className="terminal-dot" />
                    <span className="terminal-dot" />
                  </div>
                  <small className="font-monospace text-secondary">benoma.dev</small>
                </div>
                <div className="terminal-content p-4 font-monospace">
                  <p className="text-secondary">$ benoma --status</p>
                  <p>
                    <span className="text-danger">›</span> building useful things
                  </p>
                  <p>
                    <span className="text-danger">›</span> solving real problems
                  </p>
                  <p>
                    <span className="text-danger">›</span> experimenting constantly
                  </p>
                  <hr className="border-secondary my-4" />
                  <small className="text-secondary d-block mb-2">current_focus:</small>
                  <motion.h2
                    className="fs-4 fw-semibold"
                    animate={{ opacity: [1, 0.55, 1] }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    build → test → improve
                  </motion.h2>
                  <div className="terminal-status position-absolute bottom-0 start-0 end-0 d-flex justify-content-between mx-4 py-3 border-top border-secondary-subtle">
                    <small className="text-secondary">status</small>
                    <small className="text-danger">available for projects</small>
                  </div>
                </div>
                <motion.div
                  className="terminal-badge bg-danger text-white position-absolute px-3 py-3 rounded"
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <small className="d-block">Have an idea?</small>
                  <strong>We can build it.</strong>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* MARQUEE */}
      <section className="home-marquee bg-danger overflow-hidden">
        <div className="marquee-track d-flex">
          {[0, 1, 2, 3].map((group) => (
            <div
              key={group}
              className="marquee-group d-flex align-items-center flex-shrink-0"
              aria-hidden={group !== 0}
            >
              <span>WEB DEVELOPMENT</span>
              <span>•</span>
              <span>MOBILE APPS</span>
              <span>•</span>
              <span>AI & MACHINE LEARNING</span>
              <span>•</span>
              <span>AUTOMATION</span>
              <span>•</span>
              <span>DATA</span>
              <span>•</span>
              <span>EMBEDDED SYSTEMS</span>
              <span>•</span>
            </div>
          ))}
        </div>
      </section>
      {/* WHO WE ARE */}
      <section className="home-section">
        <div className="container">
          <div className="row g-4 g-lg-5">
            <div className="col-lg-4">
              <span className="section-label">01 / WHO WE ARE</span>
            </div>
            <div className="col-lg-8">
              <motion.div
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <h2 className="home-section-title fw-bold mb-4">
                  We’re curious people who like turning{" "}
                  <span className="text-secondary">difficult ideas into useful products.</span>
                </h2>
                <p className="home-section-copy mb-0">
                  Benoma started as a place to showcase the things we build. It’s becoming something
                  bigger — a creative technology collective where we experiment, collaborate and
                  help people turn ideas into working digital products.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* PROJECTS */}
      <section className="home-section home-projects">
        <div className="container">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-4 mb-5">
            <div>
              <span className="section-label">02 / SELECTED WORK</span>
              <h2 className="home-section-title fw-bold mt-3 mb-0">Things we've built.</h2>
            </div>
            <Link
              to="/projects"
              className="home-text-link d-inline-flex align-items-center gap-2 text-decoration-none"
            >
              View all projects
              <span>→</span>
            </Link>
          </div>
          <div className="row g-4">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.title}
                className="col-md-6 col-xl-4"
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                }}
              >
                <article className="home-project-card h-100 overflow-hidden rounded">
                  {/* VISUAL */}
                  <div className="project-visual position-relative overflow-hidden">
                    <div className="project-grid position-absolute top-0 start-0 w-100 h-100" />
                    <span className="project-watermark position-absolute fw-bold">
                      {project.number}
                    </span>
                    <div className="position-absolute top-0 start-0 end-0 d-flex justify-content-between p-4">
                      <small className="font-monospace text-secondary">
                        PROJECT_{project.number}
                      </small>
                      <small className="font-monospace text-secondary">BENOMA</small>
                    </div>
                    <div className="position-absolute top-50 start-50 translate-middle text-nowrap">
                      <span className="font-monospace text-danger">
                        {"< "}
                        {project.visual}
                        {" />"}
                      </span>
                    </div>
                    <div className="position-absolute bottom-0 start-0 p-4">
                      <small className="font-monospace text-secondary">SELECTED WORK</small>
                    </div>
                  </div>
                  {/* CONTENT */}
                  <div className="p-4">
                    <small className="project-category fw-bold">{project.category}</small>
                    <h3 className="fs-4 fw-bold mt-2 mb-3">{project.title}</h3>
                    <p className="text-secondary mb-4">{project.description}</p>
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {project.tech.map((tech) => (
                        <span key={tech} className="project-tech rounded-pill px-2 py-1">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <Link
                      to="/projects"
                      s
                      className="home-text-link d-inline-flex align-items-center gap-2 text-decoration-none"
                    >
                      View project
                      <span>↗</span>
                    </Link>
                  </div>
                </article>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* SERVICES */}
      <section className="home-section">
        <div className="container">
          <div className="row g-4 g-lg-5 mb-5">
            <div className="col-lg-4">
              <span className="section-label">03 / WHAT WE DO</span>
            </div>
            <div className="col-lg-8">
              <h2 className="home-section-title fw-bold mb-4">
                From rough idea to <span className="text-secondary">working product.</span>
              </h2>
              <p className="home-section-copy mb-0">
                We combine software, engineering and data to build practical products for
                individuals, teams and businesses.
              </p>
            </div>
          </div>
          <div className="border-top border-secondary-subtle">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="service-row row align-items-center g-3 py-4 border-bottom border-secondary-subtle"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                }}
              >
                <div className="col-2 col-md-1">
                  <small className="font-monospace text-danger">{service.number}</small>
                </div>
                <div className="col-10 col-md-4">
                  <h3 className="fs-4 fw-semibold mb-0">{service.title}</h3>
                </div>
                <div className="col-10 offset-2 col-md-6 offset-md-0">
                  <p className="text-secondary mb-0">{service.description}</p>
                </div>
                <div className="d-none d-md-block col-md-1 text-end">
                  <span className="service-arrow">↗</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* PHILOSOPHY */}
      <section className="home-section pt-0">
        <div className="container">
          <motion.div
            className="philosophy-card rounded p-4 p-md-5"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-label">04 / HOW WE THINK</span>
            <h2 className="philosophy-title fw-bold mt-4 mb-0">
              Good technology shouldn't just look impressive.{" "}
              <span className="text-danger">It should solve something.</span>
            </h2>
            <div className="row g-4 mt-4 mt-lg-5">
              <div className="col-md-4">
                <div className="border-top border-secondary-subtle pt-4 h-100">
                  <small className="font-monospace text-danger">01</small>
                  <h3 className="fs-4 fw-semibold mt-3">Understand</h3>
                  <p className="text-secondary mb-0">
                    We start with the actual problem, not the technology.
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border-top border-secondary-subtle pt-4 h-100">
                  <small className="font-monospace text-danger">02</small>
                  <h3 className="fs-4 fw-semibold mt-3">Build</h3>
                  <p className="text-secondary mb-0">
                    We turn ideas into usable products with the right tools.
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border-top border-secondary-subtle pt-4 h-100">
                  <small className="font-monospace text-danger">03</small>
                  <h3 className="fs-4 fw-semibold mt-3">Improve</h3>
                  <p className="text-secondary mb-0">
                    We test, learn and continuously make the product better.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* CTA */}
      <section className="pb-5 pb-lg-6">
        <div className="container">
          <motion.div
            className="home-final-cta bg-danger rounded p-4 p-md-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="row align-items-end g-4">
              <div className="col-lg-9">
                <small className="fw-bold home-cta-label">HAVE SOMETHING IN MIND?</small>
                <h2 className="home-cta-title fw-bold mt-3 mb-0">
                  Let's build something
                  <span className="d-block">worth talking about.</span>
                </h2>
              </div>
              <div className="col-lg-3 text-lg-end">
                <Link
                  to="/contact"
                  className="btn btn-light btn-lg d-inline-flex align-items-center justify-content-center gap-2"
                >
                  Start a Project
                  <span>→</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

export default Home;
